import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { api, errMsg, fmtBytes, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const COLS = [["name", "Traitement"], ["purpose", "Finalité"], ["legal_basis", "Base légale"], ["data", "Données"], ["recipients", "Destinataires"], ["retention", "Durée de conservation"]];
const STATUS = { running: "En préparation…", done: "Prêt", failed: "Échec", expired: "Expiré" };

export function ExportJobs() {
  const [jobs, setJobs] = useState([]);
  const load = () => api.get("/admin/export-jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!jobs.some((j) => j.status === "running")) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [jobs]);
  const start = async () => { try { await api.post("/admin/export-jobs"); toast.success("Export lancé, il sera prêt dans quelques minutes"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const download = async (id) => {
    try { const { data } = await api.post(`/admin/export-jobs/${id}/link`); window.location.href = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`; }
    catch (e) { toast.error(errMsg(e)); load(); }
  };
  return (
    <div className="bg-white border rounded-xl p-5 h-fit" data-testid="export-jobs">
      <h3 className="font-display font-bold">Export intégral</h3>
      <p className="text-sm text-slate-600 mt-1">Tous les documents rangés par dossier, un inventaire Excel (CSV), les métadonnées, commentaires, validations et le journal d'audit. Conservé 24 h sur le serveur.</p>
      <Button className="w-full mt-4" onClick={start} disabled={jobs.some((j) => j.status === "running")} data-testid="admin-export-btn">
        {jobs.some((j) => j.status === "running") ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}Préparer un export
      </Button>
      <div className="mt-4 divide-y">
        {jobs.map((j) => (
          <div key={j.id} className="py-2.5 flex items-center gap-2 text-sm" data-testid={`export-job-${j.id}`}>
            <div className="flex-1 min-w-0"><div className="font-medium">{fmtDate(j.created_at, true)}</div>
              <div className="text-xs text-slate-500" data-testid={`export-job-status-${j.id}`}>{STATUS[j.status]}{j.status === "done" && ` · ${j.files} fichiers · ${fmtBytes(j.size)}`}{j.missing ? ` · ${j.missing} illisibles` : ""}</div></div>
            {j.status === "done" && <Button size="sm" variant="outline" onClick={() => download(j.id)} data-testid={`export-job-download-${j.id}`}><Download className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function RegisterEditor({ rows, onChange }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-3" data-testid="rgpd-register">
      {rows.map((r, i) => (
        <div key={i} className="border rounded-lg p-3 grid md:grid-cols-2 gap-2 relative" data-testid={`rgpd-register-row-${i}`}>
          {COLS.map(([k, l]) => (
            <div key={k}><Label className="text-xs text-slate-500">{l}</Label>
              <Input value={r[k] || ""} onChange={(e) => set(i, k, e.target.value)} className="h-9" data-testid={`rgpd-register-${i}-${k}`} /></div>
          ))}
          <Button variant="ghost" size="icon" className="absolute top-1 right-1 h-7 w-7" aria-label="Supprimer" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`rgpd-register-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...rows, Object.fromEntries(COLS.map(([k]) => [k, ""]))])} data-testid="rgpd-register-add"><Plus className="h-4 w-4 mr-1" />Ajouter un traitement</Button>
    </div>
  );
}

function downloadRegister(rows) {
  const esc = (v) => `"${String(v || "").replace(/"/g, '""')}"`;
  const csv = "\ufeff" + [COLS.map(([, l]) => esc(l)).join(";"), ...rows.map((r) => COLS.map(([k]) => esc(r[k])).join(";"))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = "registre_des_traitements.csv"; a.click();
}

export function AdminRgpd() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/rgpd").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;
  const save = async () => {
    const days = s.audit_retention_days ? Number(s.audit_retention_days) : null;
    if (days !== null && (days < 180 || days > 3650)) { toast.error("La durée du journal doit être comprise entre 180 et 3650 jours (ou vide)."); return; }
    if ((s.notice_text || "").trim().length < 20) { toast.error("La notice d'information est trop courte."); return; }
    if (s.register.some((r) => !r.name?.trim())) { toast.error("Chaque traitement du registre doit avoir un nom."); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/admin/rgpd", { controller: s.controller, dpo_email: s.dpo_email, notice_text: s.notice_text,
        audit_retention_days: days, register: s.register });
      setS({ ...s, ...data }); toast.success("Paramètres RGPD enregistrés");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border rounded-xl p-5 space-y-5" data-testid="admin-rgpd">
      <div className="flex items-center justify-between gap-3"><h3 className="font-display font-bold">Paramètres RGPD</h3>
        <Button onClick={save} disabled={busy} data-testid="rgpd-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button></div>
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label className="text-xs">Responsable du traitement</Label><Input value={s.controller} onChange={(e) => setS({ ...s, controller: e.target.value })} data-testid="rgpd-controller-input" /></div>
        <div><Label className="text-xs">Contact RGPD</Label><Input value={s.dpo_email} onChange={(e) => setS({ ...s, dpo_email: e.target.value })} data-testid="rgpd-dpo-input" /></div>
        <div><Label className="text-xs">Durée du journal d'audit (jours, vide = illimité)</Label>
          <Input type="number" min={180} max={3650} value={s.audit_retention_days ?? ""} onChange={(e) => setS({ ...s, audit_retention_days: e.target.value })} placeholder="Illimitée" data-testid="rgpd-retention-input" /></div>
      </div>
      <div><Label className="text-xs">Notice d'information affichée aux utilisateurs (version {s.notice_version} ; la modifier la fait relire à tous)</Label>
        <Textarea rows={7} value={s.notice_text} onChange={(e) => setS({ ...s, notice_text: e.target.value })} data-testid="rgpd-notice-input" /></div>
      <div>
        <div className="flex items-center justify-between mb-2"><h4 className="font-semibold text-sm">Registre des traitements (art. 30)</h4>
          <Button variant="ghost" size="sm" onClick={() => downloadRegister(s.register)} data-testid="rgpd-register-download"><Download className="h-4 w-4 mr-1" />Exporter (Excel)</Button></div>
        <RegisterEditor rows={s.register} onChange={(register) => setS({ ...s, register })} />
      </div>
    </div>
  );
}
